import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  AlertTriangle,
  Calendar,
  Eye,
  RotateCcw,
  Trash2
} from 'lucide-react';
import clientService from '../services/clientService';
import CustomSelect from '../components/common/CustomSelect';

const ClientTrashPage = () => {
  const navigate = useNavigate();
  
  const [deletedClients, setDeletedClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    type: '', // PERSON, COMPANY, ''
    dateFrom: '',
    dateTo: ''
  });

  const loadDeletedClients = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await clientService.getDeletedClients({
        page,
        limit: itemsPerPage,
        search: searchTerm,
        type: filters.type,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo
      });
      setDeletedClients(response.data?.clients || []);
      setTotalPages(response.data?.pagination?.totalPages || 1);
      setTotalItems(response.data?.pagination?.totalItems || 0);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error cargando clientes eliminados:', error);
      alert('Error al cargar la papelera');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, itemsPerPage]);

  const handleRestoreClient = async (clientId, clientName) => {
    const confirmed = window.confirm(
      `¿Estás seguro de que quieres restaurar el cliente "${clientName}"?\n\nEl cliente volverá a estar activo y visible en la lista principal.`
    );
    
    if (confirmed) {
      try {
        await clientService.restoreClient(clientId);
        alert('El cliente ha sido restaurado exitosamente y ya está disponible en la lista principal.');
        loadDeletedClients();
      } catch (error) {
        console.error('Error restaurando cliente:', error);
        alert('Error al restaurar el cliente. Por favor, inténtalo de nuevo.');
      }
    }
  };

  const handleViewClient = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  // Navegación de páginas
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      loadDeletedClients(newPage);
    }
  };

  // Cambiar página anterior
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  // Cambiar página siguiente
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Manejar cambio de registros por página
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    loadDeletedClients(1);
  };

  useEffect(() => {
    loadDeletedClients(1);
  }, [loadDeletedClients]);

  // Recargar cuando cambien los filtros o búsqueda
  useEffect(() => {
    if (currentPage === 1) {
      loadDeletedClients(1);
    } else {
      setCurrentPage(1);
      loadDeletedClients(1);
    }
  }, [searchTerm, filters]);

  // Función para normalizar texto (eliminar tildes y convertir a minúsculas)
  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, ''); // Eliminar tildes y acentos
  };

  // Filtrar clientes localmente con normalización
  const filteredClients = deletedClients.filter(client => {
    // Filtro de búsqueda por texto
    if (searchTerm && searchTerm.trim()) {
      const normalizedSearchTerm = normalizeText(searchTerm);
      const searchableFields = [
        client.name,
        client.email,
        client.phone,
        client.contactPerson,
        client.company
      ].filter(Boolean);
      
      const matchesSearch = searchableFields.some(field => 
        normalizeText(field).includes(normalizedSearchTerm)
      );
      if (!matchesSearch) return false;
    }
    
    // Filtro por tipo de cliente
    if (filters.type && client.clientType !== filters.type) {
      return false;
    }
    
    // Filtro por fecha de eliminación
    if (filters.dateFrom || filters.dateTo) {
      const clientDate = new Date(client.deletedAt);
      const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : null;
      const toDate = filters.dateTo ? new Date(filters.dateTo) : null;
      
      if (fromDate && clientDate < fromDate) return false;
      if (toDate && clientDate > toDate) return false;
    }
    
    return true;
  });

  // Manejar filtros
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Limpiar todos los filtros
  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      type: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  return (
    <div className="animate-fadeIn">
      {/* Header de Navegación */}
      <div className="flex items-center mb-6">
        <button
          className="btn btn-secondary btn-icon"
          onClick={() => navigate('/clients')}
        >
          <ArrowLeft size={20} />
          Volver a Clientes
        </button>
      </div>

      {/* Título y Descripción */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Papelera de Clientes</h1>
        <p className="text-gray-600">
          Aquí puedes ver y restaurar clientes que han sido eliminados. 
          Los clientes en la papelera pueden ser restaurados en cualquier momento.
        </p>
      </div>

      {/* Estadísticas de Papelera */}
      <div className="card mb-8">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Estadísticas de Papelera</h2>
        </div>
        <div className="card-content">
          <div className="flex gap-4 justify-center">
            {/* Card Total Eliminados */}
            <div 
              className="rounded-lg p-4 w-48 border"
              style={{ 
                backgroundColor: '#fef2f2', 
                borderColor: '#fca5a5',
                color: '#dc2626'
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium" style={{ color: '#dc2626' }}>Clientes Eliminados</div>
                  <div className="text-xl font-bold" style={{ color: '#991b1b' }}>{deletedClients?.length || 0}</div>
                </div>
                <div className="text-2xl">🗑️</div>
              </div>
            </div>

            {/* Card Empresas Eliminadas */}
            <div 
              className="rounded-lg p-4 w-48 border"
              style={{ 
                backgroundColor: '#faf5ff', 
                borderColor: '#c4b5fd',
                color: '#7c3aed'
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium" style={{ color: '#8b5cf6' }}>Empresas Eliminadas</div>
                  <div className="text-xl font-bold" style={{ color: '#6b21a8' }}>
                    {deletedClients?.filter(client => client && client.clientType === 'COMPANY')?.length || 0}
                  </div>
                </div>
                <div className="text-2xl">🏢</div>
              </div>
            </div>

            {/* Card Personas Eliminadas */}
            <div 
              className="rounded-lg p-4 w-48 border"
              style={{ 
                backgroundColor: '#eef2ff', 
                borderColor: '#a5b4fc',
                color: '#4338ca'
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium" style={{ color: '#6366f1' }}>Personas Eliminadas</div>
                  <div className="text-xl font-bold" style={{ color: '#3730a3' }}>
                    {deletedClients?.filter(client => client && client.clientType === 'PERSON')?.length || 0}
                  </div>
                </div>
                <div className="text-2xl">👤</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenedor de filtros */}
      <div className="card mb-6 filters-container">
        <div className="card-content">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Campo de búsqueda */}
            <div className="flex-1 min-w-64">
              <label className="form-label">Buscar</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nombre, email o teléfono..."
                className="form-input"
              />
            </div>

            {/* Filtro por tipo */}
            <div className="w-48">
              <CustomSelect
                label="Tipo"
                value={filters.type}
                onChange={(value) => handleFilterChange('type', value)}
                options={[
                  { value: '', label: 'Todos los tipos' },
                  { value: 'PERSON', label: 'Persona Natural' },
                  { value: 'COMPANY', label: 'Empresa' }
                ]}
                placeholder="Todos los tipos"
              />
            </div>

            {/* Filtro por fecha desde */}
            <div className="w-48">
              <label className="form-label">Fecha desde</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="form-input"
              />
            </div>

            {/* Filtro por fecha hasta */}
            <div className="w-48">
              <label className="form-label">Fecha hasta</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="form-input"
              />
            </div>

            {/* Botón limpiar */}
            <div className="w-48">
              <label className="form-label">&nbsp;</label>
              <button
                onClick={clearFilters}
                className="btn btn-outline w-full"
              >
                🗑️ Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Indicador de resultados */}
      {(searchTerm || filters.type || filters.dateFrom || filters.dateTo) && (
        <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <p className="text-sm text-orange-700">
            Se encontraron {filteredClients.length} resultado(s)
            {searchTerm && ` para "${searchTerm}"`}
            {filters.type && ` | Tipo: ${filters.type === 'PERSON' ? 'Persona Natural' : 'Empresa'}`}
            {filters.dateFrom && ` | Desde: ${new Date(filters.dateFrom).toLocaleDateString()}`}
            {filters.dateTo && ` | Hasta: ${new Date(filters.dateTo).toLocaleDateString()}`}
          </p>
        </div>
      )}

      {/* Selector de registros por página */}
      <div className="records-per-page">
        <label htmlFor="itemsPerPage">Registros por página:</label>
        <select
          id="itemsPerPage"
          value={itemsPerPage}
          onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>

      {/* Lista de clientes eliminados */}
      <div className="table-container">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="loading-spinner"></div>
            <p className="mt-4 text-gray-600">Cargando papelera...</p>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-6xl mb-4">🗑️</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {(searchTerm || filters.type || filters.dateFrom || filters.dateTo) ? 'No se encontraron resultados' : 'Papelera vacía'}
              </h3>
              <p className="text-gray-600 mb-6">
                {(searchTerm || filters.type || filters.dateFrom || filters.dateTo) ? 
                  'No hay clientes eliminados que coincidan con los filtros aplicados' : 
                  'No hay clientes eliminados para mostrar'
                }
              </p>
            <button
              onClick={() => navigate('/clients')}
              className="btn btn-primary"
            >
              Volver a Clientes
            </button>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Email</th>
                <th>Teléfono</th>
                <th>Tipo</th>
                <th>Eliminado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => (
                <tr key={client._id}>
                  <td>
                    <div>
                      <div className="font-medium text-gray-900">{client.name || 'Sin nombre'}</div>
                      <div className="text-sm text-gray-500">ID: {client._id}</div>
                    </div>
                  </td>
                  <td className="text-sm text-gray-700">{client.email || 'Sin email'}</td>
                  <td className="text-sm text-gray-700">{client.phone || 'Sin teléfono'}</td>
                  <td>
                    <span className={`badge ${client.clientType === 'COMPANY' ? 'badge-primary' : 'badge-secondary'}`}>
                      {client.clientType === 'COMPANY' ? 'Empresa' : 'Persona Natural'}
                    </span>
                  </td>
                  <td className="text-sm text-gray-700">
                    <div className="flex items-center">
                      <Calendar size={14} className="mr-1" />
                      {new Date(client.deletedAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewClient(client._id)}
                        className="btn btn-primary btn-sm"
                        title="Ver perfil"
                      >
                        <Eye size={16} /> Ver
                      </button>
                      <button
                        onClick={() => handleRestoreClient(client._id, client.name)}
                        className="btn btn-success btn-sm"
                        title="Restaurar cliente"
                      >
                        <RotateCcw size={16} /> Restaurar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>
              Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} clientes eliminados
            </span>
          </div>
          <div className="pagination-controls">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="btn btn-outline btn-sm"
            >
              ← Anterior
            </button>
            
            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`btn btn-sm ${currentPage === pageNum ? 'btn-primary' : 'btn-outline'}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="btn btn-outline btn-sm"
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}

      {/* Información importante */}
      <div style={{ marginTop: '48px' }}>
        <div style={{ 
          backgroundColor: '#fef3c7', 
          border: '1px solid #f59e0b', 
          borderRadius: '8px', 
          padding: '16px' 
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <AlertTriangle size={16} style={{ color: '#d97706', marginTop: '2px', flexShrink: 0 }} />
            <div>
              <p style={{ fontSize: '14px', color: '#92400e', margin: 0 }}>
                <strong>Importante:</strong> Los clientes en la papelera pueden ser restaurados en cualquier momento. 
                Los datos se mantienen seguros y pueden recuperarse cuando sea necesario.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientTrashPage;
